import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { resolveImageUrl } from '@/utils/imageUrl';
import {
  Newspaper, BookOpen, MessageSquareQuote, Bookmark,
  ArrowRight, Eye, ChevronRight, ThumbsUp,
} from 'lucide-react';

/**
 * useFeedIntelligence — fetches a personalised mix of news, blogs, and Q&A
 * cards for inline injection in the social feed. Returns a single ordered
 * array, round-robin interleaved across types so users always see variety.
 *
 * NOTE: We deliberately do NOT filter by user's home country — blogs/news
 * are tagged by *destination* country (Australia, Canada, UK, etc.), so a
 * student living in India would see 0 results. When destination/visa intent
 * tracking lands, we'll filter by that instead.
 */
export function useFeedIntelligence() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Fetch news/blog/qa in parallel with the primary /api/posts request.
    // Historic 1s setTimeout defer (Mar 2026) was removed on Jun 29, 2026
    // because it created a race: mirror-source posts (news_auto_publish /
    // blog_auto_publish) rendered as intel cards before intelItems arrived,
    // producing duplicate cards + wrong priority order until the ring
    // rebuilt post-fetch. HTTP/2 handles parallel requests fine.
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/blog/posts?content_type=news&limit=15`).catch(() => ({ data: { posts: [] } })),
      axios.get(`${API}/blog/posts?content_type=blog&limit=15`).catch(() => ({ data: { posts: [] } })),
      axios.get(`${API}/qa/public?limit=15`).catch(() => ({ data: { questions: [] } })),
    ]).then(([n, b, q]) => {
      if (cancelled) return;
      const news = (n.data?.posts || []).map((p) => ({ type: 'news', ...p }));
      const blog = (b.data?.posts || []).map((p) => ({ type: 'blog', ...p }));
      const qa = (q.data?.questions || []).map((p) => ({ type: 'qa', ...p }));
      const max = Math.max(news.length, blog.length, qa.length);
      const mix = [];
      for (let i = 0; i < max; i++) {
        if (news[i]) mix.push(news[i]);
        if (blog[i]) mix.push(blog[i]);
        if (qa[i]) mix.push(qa[i]);
      }
      setItems(mix);
    });
    return () => { cancelled = true; };
  }, []);

  return items;
}

/** Single dispatcher — picks the right card by item.type. */
export function FeedIntelCard({ item }) {
  if (!item) return null;
  if (item.type === 'news') return <NewsCard item={item} />;
  if (item.type === 'blog') return <BlogCard item={item} />;
  if (item.type === 'qa') return <QACard item={item} />;
  return null;
}

/* ─── NEWS CARD ─── orange tint */
function NewsCard({ item }) {
  const navigate = useNavigate();
  const cover = resolveImageUrl(item.cover_image) || '';
  return (
    <article
      className="rounded-2xl overflow-hidden border border-orange-200 bg-white shadow-sm hover:shadow-md transition-shadow"
      data-testid={`feed-news-${item.article_id}`}
    >
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-2 flex items-center justify-between border-b border-orange-100">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
            <Newspaper className="h-3 w-3 text-orange-600" />
          </div>
          <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">
            News · {item.category || 'Update'}
          </span>
        </div>
        <span className="text-[10px] text-orange-500 font-medium">
          {item.read_time || 2} min read
        </span>
      </div>
      <div className="flex gap-3 p-4">
        {cover ? (
          <img
            src={cover}
            alt=""
            className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center flex-shrink-0">
            <Newspaper className="h-8 w-8 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-[#0A2540] line-clamp-2 leading-snug">
            {item.title}
          </h3>
          <p className="text-xs text-slate-500 line-clamp-2 mt-1">
            {item.summary}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between px-4 pb-3">
        <button
          onClick={() => navigate(`/news/${item.slug}`)}
          className="text-xs font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1 transition-colors"
          data-testid={`news-cta-${item.article_id}`}
        >
          Read full update <ArrowRight className="h-3 w-3" />
        </button>
        <button
          className="text-slate-400 hover:text-orange-500 transition-colors"
          data-testid={`news-save-${item.article_id}`}
          aria-label="Save"
        >
          <Bookmark className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}

/* ─── BLOG CARD ─── blue tint
   Jun 20, 2026 — re-laid out to mirror NewsCard horizontal layout:
   small left thumbnail + right-side text. Keeps blue chip / colours /
   "Continue reading →" link / bookmark icon. */
function BlogCard({ item }) {
  const navigate = useNavigate();
  const cover = resolveImageUrl(item.cover_image) || '';
  return (
    <article
      className="rounded-2xl overflow-hidden border border-blue-200 bg-white shadow-sm hover:shadow-md transition-shadow"
      data-testid={`feed-blog-${item.article_id}`}
    >
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 flex items-center justify-between border-b border-blue-100">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
            <BookOpen className="h-3 w-3 text-blue-600" />
          </div>
          <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">
            Blog · For You
          </span>
        </div>
        <span className="text-[10px] text-blue-500 font-medium">
          {item.read_time || 5} min read
        </span>
      </div>
      <div className="flex gap-3 p-4">
        {cover ? (
          <img
            src={cover}
            alt=""
            className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-[#0A2540] line-clamp-2 leading-snug">
            {item.title}
          </h3>
          <p className="text-xs text-slate-500 line-clamp-2 mt-1">
            {item.summary}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-slate-400">
            <span className="font-medium">By {item.author_display || item.author_name || 'Unexa Team'}</span>
            {item.category && <><span>·</span><span>{item.category}</span></>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between px-4 pb-3">
        <button
          onClick={() => navigate(`/blog/${item.slug}`)}
          className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
          data-testid={`blog-cta-${item.article_id}`}
        >
          Continue reading <ArrowRight className="h-3 w-3" />
        </button>
        <button
          className="text-slate-400 hover:text-blue-500 transition-colors"
          data-testid={`blog-save-${item.article_id}`}
          aria-label="Save"
        >
          <Bookmark className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}

/* ─── Q&A CARD ─── green tint */
function QACard({ item }) {
  const navigate = useNavigate();
  // Route to the Q&A tab on the Newsboard/Explore content surface — the
  // dedicated /qa/* routes were never wired, so we keep the user inside the
  // known /blog?tab=qa surface which already renders the full Q&A list.
  // Append #qa-{id} so the destination page can scroll/highlight the row.
  const link = `/blog?tab=qa#qa-${item.question_id}`;
  const answerSnippet = item.answer_text ? String(item.answer_text).slice(0, 140) : '';
  return (
    <article
      className="rounded-2xl overflow-hidden border border-emerald-200 bg-white shadow-sm hover:shadow-md transition-shadow"
      data-testid={`feed-qa-${item.question_id}`}
    >
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-2 flex items-center justify-between border-b border-emerald-100">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
            <MessageSquareQuote className="h-3 w-3 text-emerald-600" />
          </div>
          <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
            Q&A · {item.category || 'Trending'}
          </span>
        </div>
        {item.helpful_count > 0 && (
          <span className="text-[10px] text-emerald-500 font-medium flex items-center gap-1">
            <ThumbsUp className="h-3 w-3" />{item.helpful_count}
          </span>
        )}
      </div>
      <div className="px-4 pt-3 pb-2">
        <p className="text-sm font-semibold text-[#0A2540] leading-snug">
          <span className="text-emerald-500 mr-1">"</span>
          {item.question_text}
          <span className="text-emerald-500 ml-1">"</span>
        </p>
        <p className="text-[11px] text-slate-400 mt-1.5">
          — Asked by <span className="font-medium text-slate-500">{item.student_name || 'a student'}</span>
        </p>
      </div>
      {answerSnippet && (
        <div className="mx-4 mb-3 p-2.5 rounded-lg bg-emerald-50/50 border border-emerald-100">
          <div className="flex items-start gap-2">
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider shrink-0 mt-0.5">
              ✓ Top answer
            </span>
          </div>
          <p className="text-xs text-slate-600 line-clamp-2 mt-1 leading-relaxed">
            {answerSnippet}{item.answer_text?.length > 140 ? '…' : ''}
          </p>
          {item.answered_by_name && (
            <p className="text-[10px] text-slate-400 mt-1.5">
              by <span className="font-medium">{item.answered_by_name}</span>
            </p>
          )}
        </div>
      )}
      <div className="flex items-center justify-between px-4 pb-3">
        <button
          onClick={() => navigate(link)}
          className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
          data-testid={`qa-cta-${item.question_id}`}
        >
          See full answer <ArrowRight className="h-3 w-3" />
        </button>
        {item.view_count > 0 && (
          <span className="text-[10px] text-slate-400 flex items-center gap-1">
            <Eye className="h-3 w-3" />{item.view_count}
          </span>
        )}
      </div>
    </article>
  );
}
